import { useEffect, useState } from 'react'
import { useCart } from '../cart'
import { useT } from '../i18n'
import { usePriceFormat } from '../shopConfig'

/**
 * Die Eingabe für einen Rabattcode.
 *
 * # Warum der Code stehen bleibt, auch wenn er nicht greift
 *
 * Ein Code kann gültig sein und trotzdem auf diesen Korb nicht passen: Der
 * Mindestbestellwert ist noch nicht erreicht, oder er gilt nur für Kaffee und
 * es liegt keiner drin. Ihn dann zu verwerfen hieße, den Kunden zweimal tippen
 * zu lassen — einmal jetzt und einmal, wenn er den Kaffee dazugelegt hat.
 *
 * Deshalb bleibt der Code am Warenkorb stehen, und der Server sagt bei jedem
 * Anzeigen neu, ob er greift. Was dabei herauskommt, steht in einem fertigen
 * Satz in `discountProblem` — er kennt den Mindestbestellwert dieses Ladens,
 * das Ladenfenster nicht.
 *
 * # Warum zugeklappt, solange keiner da ist
 *
 * Ein offenes Feld „Rabattcode" ist eine Aufforderung, die Seite zu verlassen
 * und einen zu suchen. Wer einen hat, findet die Zeile; wer keinen hat, soll
 * nicht daran erinnert werden, dass es welche gibt.
 */
export function Rabattfeld() {
  const t = useT()
  const { ansicht, arbeitet, setzeRabatt } = useCart()
  const preis = usePriceFormat()

  const gilt = ansicht.discountGross > 0
  const [offen, setOffen] = useState(false)
  const [eingabe, setEingabe] = useState(ansicht.discountCode)

  // Der Server ist die Wahrheit: Wurde der Code anderswo geändert — in einem
  // zweiten Tab, oder weil die Kasse ihn beim Bestellen verbraucht hat —,
  // zieht das Feld nach.
  useEffect(() => {
    setEingabe(ansicht.discountCode)
  }, [ansicht.discountCode])

  function absenden() {
    const code = eingabe.trim().toUpperCase()
    if (code === '' || code === ansicht.discountCode) return
    setzeRabatt(code)
  }

  // Liegt ein Rabatt an, steht dort keine Eingabe mehr, sondern was er bringt.
  if (gilt) {
    return (
      <div className="rabatt rabatt-gilt">
        <span className="rabatt-marke">{ansicht.discountCode}</span>
        <span className="rabatt-name">{ansicht.discountName || t('rabatt.gewaehrt')}</span>
        <strong className="rabatt-betrag">−{preis(ansicht.discountGross)}</strong>
        <button
          className="verweisknopf"
          type="button"
          disabled={arbeitet}
          onClick={() => setzeRabatt('')}
        >
          {t('rabatt.entfernen')}
        </button>
      </div>
    )
  }

  // Ein eingetragener Code, der gerade nicht greift, klappt die Zeile von
  // selbst auf: Sonst stünde die Begründung unter einem zugeklappten Feld.
  const zeigen = offen || ansicht.discountProblem !== '' || ansicht.discountCode !== ''

  if (!zeigen) {
    return (
      <button className="verweisknopf rabatt-auf" type="button" onClick={() => setOffen(true)}>
        {t('rabatt.haben')}
      </button>
    )
  }

  return (
    /* Ein <div> und kein <form> — aus demselben Grund wie beim
       Gutscheinfeld: Dieses Feld steht heute in der Übersicht neben dem
       Bestellformular, aber ein Bauteil darf nicht davon abhängen, wo es
       eingesetzt wird. Ein <form> im <form> wirft der Browser weg, und der
       Knopf löste dann die Bestellung aus. */
    <div className="rabatt">
      <label className="nur-fuer-screenreader" htmlFor="rabattcode">
        {t('rabatt.label')}
      </label>
      <input
        id="rabattcode"
        className="rabatt-eingabe"
        type="text"
        inputMode="text"
        autoComplete="off"
        /* Großbuchstaben beim Tippen: Codes stehen so auf dem Flyer, und eine
           Eingabe, die anders aussieht als die Vorlage, lässt zweifeln. */
        autoCapitalize="characters"
        spellCheck={false}
        placeholder={t('rabatt.platzhalter')}
        value={eingabe}
        disabled={arbeitet}
        onChange={(ereignis) => setEingabe(ereignis.target.value.toUpperCase())}
        onKeyDown={(ereignis) => {
          if (ereignis.key !== 'Enter') return
          ereignis.preventDefault()
          ereignis.stopPropagation()
          absenden()
        }}
        aria-describedby={ansicht.discountProblem ? 'rabatt-problem' : undefined}
        aria-invalid={ansicht.discountProblem !== '' || undefined}
      />
      <button
        className="knopf knopf-schlicht"
        type="button"
        disabled={arbeitet || eingabe.trim() === ''}
        onClick={absenden}
      >
        {t('rabatt.einloesen')}
      </button>

      {/* Der Satz des Servers unverändert: Er kennt den Mindestbestellwert und
          die Artikel, für die der Code gilt. */}
      {ansicht.discountProblem !== '' && (
        <p className="hinweis hinweis-fehler rabatt-problem" id="rabatt-problem" role="status">
          {ansicht.discountProblem}{' '}
          <button className="verweisknopf" type="button" onClick={() => setzeRabatt('')}>
            {t('rabatt.entfernen')}
          </button>
        </p>
      )}
    </div>
  )
}
